import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

type Task = { id: string; title: string; status: string; created_at: string; is_demo: boolean };
type Usage = {
  runs: number; prompt_tokens: number; completion_tokens: number; tool_calls: number;
  approvals: number; sms_segments: number; estimated_cost_usd: number; period_start: string;
};

const STATUS_COLOR: Record<string, string> = {
  completed: "text-emerald-500", failed: "text-destructive", cancelled: "text-muted-foreground",
  waiting_for_approval: "text-amber-500", working: "text-primary",
};

export default function Tasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);

  useEffect(() => {
    api.get("/tasks").then((r) => setTasks(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/tasks/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="tasks-page">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Tasks</h1>
      <p className="text-sm text-muted-foreground">What Remy is working on for you.</p>

      {usage && (
        <div className="mt-4 grid grid-cols-3 gap-3" data-testid="usage-summary">
          {[["Runs today", usage.runs], ["Tool calls", usage.tool_calls],
            ["Approvals", usage.approvals], ["SMS segments", usage.sms_segments],
            ["Tokens", usage.prompt_tokens + usage.completion_tokens],
            ["Est. cost", `$${usage.estimated_cost_usd.toFixed(4)}`]].map(([label, val]) => (
            <div key={label as string} className="rounded-2xl border border-border bg-card p-3 text-center">
              <div className="text-lg font-semibold">{val as any}</div>
              <div className="text-[11px] text-muted-foreground">{label as string}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 space-y-2">
        {tasks.length === 0 && <p className="text-sm text-muted-foreground">No tasks yet.</p>}
        {tasks.map((t) => (
          <div key={t.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`task-${t.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">{t.title}</span>
              <span className={`text-[11px] font-semibold uppercase tracking-wide ${STATUS_COLOR[t.status] || "text-muted-foreground"}`}>
                {t.status.replace(/_/g, " ")}
              </span>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">{new Date(t.created_at).toLocaleString()}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
